/**
 * DataNormalizer.js
 * 
 * A utility module for standardizing data formats from various API responses.
 * Handles different data shapes from TAAPI and other providers to create a 
 * consistent format with proper value and timestamp fields.
 */

/**
 * Normalizes raw indicator data into a consistent format
 * 
 * @param {*} data - Raw data from API response (can be various formats)
 * @returns {Array} - Array of normalized data points with consistent structure
 */
export const normalizeIndicatorData = (data) => {
  // Handle null or undefined data
  if (!data) {
    console.log('[DataNormalizer] No data provided for normalization');
    return [];
  }

  // Already an array of properly structured objects
  if (Array.isArray(data) && data.length > 0) {
    // Check if array contains already normalized objects
    if (data[0]?.value !== undefined && data[0]?.timestamp !== undefined) {
      console.log('[DataNormalizer] Data already in normalized format');
      return data;
    }

    // Handle array-formatted values from TAAPI API (new format with results parameter)
    if (data.length === 1 && 
        data[0]?.result?.value && 
        Array.isArray(data[0].result.value) && 
        Array.isArray(data[0].result.timestamp) &&
        data[0].result.value.length === data[0].result.timestamp.length) {
      
      console.log(`[DataNormalizer] Transforming array-formatted values with ${data[0].result.value.length} points`);
      const values = data[0].result.value;
      const timestamps = data[0].result.timestamp;
      
      // Transform to the expected format
      return values.map((value, index) => ({
        value: value,
        timestamp: timestamps[index],
        metadata: {
          originalFormat: 'taapi_parallel_arrays',
          normalizedAt: Date.now()
        }
      }));
    }

    // Process array of items that need individual normalization
    console.log(`[DataNormalizer] Processing array of ${data.length} items`);
    return data.map(item => normalizeSingleDataPoint(item));
  }

  // Single item that needs normalization
  console.log('[DataNormalizer] Processing single item');
  const normalized = normalizeSingleDataPoint(data);
  return normalized ? [normalized] : [];
};

/**
 * Normalizes a single data point to a standard format
 * 
 * @param {*} dataPoint - Single data point in any supported format
 * @returns {Object|null} - Normalized data point or null if invalid
 */
export const normalizeSingleDataPoint = (dataPoint) => {
  // Handle direct numeric value
  if (typeof dataPoint === 'number') {
    return {
      value: dataPoint,
      timestamp: Date.now(),
      metadata: {
        originalFormat: 'numeric_value',
        normalizedAt: Date.now(),
        synthetic: true // Flag as synthetic since we're adding a current timestamp
      }
    };
  }

  // Handle null/undefined
  if (!dataPoint) {
    console.log('[DataNormalizer] Empty data point provided');
    return null;
  }

  // Standard result object format from TAAPI
  if (dataPoint?.result?.value !== undefined) {
    return {
      value: dataPoint.result.value,
      timestamp: dataPoint.result.timestamp || Date.now(),
      metadata: {
        originalFormat: 'taapi_result_object',
        normalizedAt: Date.now(),
        ...dataPoint.metadata // Preserve any existing metadata
      }
    };
  }

  // Direct value property format
  if (dataPoint?.value !== undefined) {
    return {
      value: dataPoint.value,
      timestamp: dataPoint.timestamp || Date.now(),
      metadata: {
        originalFormat: 'value_property_object',
        normalizedAt: Date.now(),
        ...dataPoint.metadata // Preserve any existing metadata
      }
    };
  }

  // Candle format (OHLCV)
  if (dataPoint?.close !== undefined) {
    return {
      value: dataPoint.close, // Default to close price
      timestamp: dataPoint.timestamp || Date.now(),
      open: dataPoint.open,
      high: dataPoint.high,
      low: dataPoint.low,
      close: dataPoint.close,
      volume: dataPoint.volume,
      metadata: {
        originalFormat: 'ohlcv_candle',
        normalizedAt: Date.now(),
        ...dataPoint.metadata // Preserve any existing metadata
      }
    };
  }

  // Last fallback for unknown formats - check all properties for a potential value
  console.log('[DataNormalizer] Unrecognized data format, attempting to extract any numeric value');
  const numericProps = Object.entries(dataPoint)
    .filter(([key, val]) => typeof val === 'number' && key !== 'timestamp')
    .map(([key, val]) => ({ key, val }));
  
  if (numericProps.length > 0) {
    // Take the first numeric property or the one named 'value' if it exists
    const valueEntry = numericProps.find(p => p.key === 'value') || numericProps[0];
    
    return {
      value: valueEntry.val,
      timestamp: dataPoint.timestamp || Date.now(),
      metadata: {
        originalFormat: 'extracted_numeric',
        extractedFrom: valueEntry.key,
        normalizedAt: Date.now(),
        allNumericProps: numericProps.map(p => p.key),
        warning: 'Format not explicitly supported, best-effort extraction'
      }
    };
  }

  console.log('[DataNormalizer] Unable to normalize data point:', JSON.stringify(dataPoint));
  return null;
};

/**
 * Validates a timestamp to ensure it's a valid date
 * 
 * @param {number|string} timestamp - The timestamp to validate
 * @returns {boolean} - Whether the timestamp is valid
 */
export const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false;
  
  // Convert to number if it's a string
  const numTimestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
  
  // Check if it's a valid number
  if (isNaN(numTimestamp)) return false;
  
  // Create a date object and check if it's valid
  const date = new Date(numTimestamp);
  
  // Check if the date is valid and within a reasonable range (1970-2100)
  return date instanceof Date && 
         !isNaN(date) && 
         date.getTime() > 0 && 
         date.getFullYear() >= 1970 && 
         date.getFullYear() <= 2100;
};

/**
 * Extracts timestamps from candle data with validation
 * 
 * @param {Array} candles - Array of candle data
 * @returns {Array} - Array of valid timestamps
 */
export const extractValidTimestamps = (candles) => {
  if (!candles || !Array.isArray(candles) || candles.length === 0) {
    console.log('[Timestamp Extraction] No valid candle data available');
    return [];
  }
  
  // Extract timestamps from candles with validation
  const timestamps = candles.map((candle, index) => {
    let timestamp = null;
    
    // Try to extract timestamp from different possible properties
    if (candle.opentime && isValidTimestamp(candle.opentime)) {
      timestamp = candle.opentime;
    } else if (candle.time && isValidTimestamp(candle.time)) {
      timestamp = candle.time;
    } else if (candle.timestamp && isValidTimestamp(candle.timestamp)) {
      timestamp = candle.timestamp;
    }
    
    if (!timestamp) {
      console.warn(`[Timestamp Extraction] WARNING: Invalid timestamp at index ${index}`);
    }
    
    return timestamp;
  }).filter(t => t !== null);
  
  if (timestamps.length === 0) {
    console.log('[Timestamp Extraction] No valid timestamps found in candle data');
    return [];
  }
  
  // Sort timestamps chronologically
  timestamps.sort((a, b) => a - b);
  
  // Log timestamp information
  console.log(`[Timestamp Extraction] Extracted ${timestamps.length} valid timestamps`);
  console.log(`[Timestamp Extraction] First timestamp: ${new Date(timestamps[0]).toISOString()}`);
  console.log(`[Timestamp Extraction] Last timestamp: ${new Date(timestamps[timestamps.length-1]).toISOString()}`);
  
  return timestamps;
};

/**
 * Extracts just the values from normalized data points
 * 
 * @param {Array} normalizedData - Array of normalized data points
 * @returns {Array} - Array of numeric values
 */
export const extractValues = (normalizedData) => {
  if (!Array.isArray(normalizedData)) {
    console.log('[DataNormalizer] extractValues: Input is not an array');
    return [];
  }
  
  // Debug information about the data
  if (normalizedData.length > 0) {
    console.log(`[DataNormalizer] extractValues: Processing ${normalizedData.length} items, first item type: ${typeof normalizedData[0]}`);
    
    // Log the first item structure
    if (typeof normalizedData[0] === 'object') {
      console.log('[DataNormalizer] First item keys:', Object.keys(normalizedData[0]));
    }
  } else {
    console.log('[DataNormalizer] extractValues: Empty array provided');
    return [];
  }
  
  const values = normalizedData
    .map(item => {
      // Handle direct numeric value
      if (typeof item === 'number') {
        return item;
      }
      
      // Handle objects
      if (item && typeof item === 'object') {
        // Standard normalized format
        if (item.value !== undefined) {
          return item.value;
        }
        
        // Handle result.value format
        if (item.result && item.result.value !== undefined) {
          return item.result.value;
        }
        
        // Handle OHLC format, default to close
        if (item.close !== undefined) {
          return item.close;
        }
        
        // Try to find any numeric property
        const numericProps = Object.entries(item)
          .filter(([key, val]) => typeof val === 'number' && key !== 'timestamp');
        
        if (numericProps.length > 0) {
          // Prefer properties with indicator-like names
          const valueProperties = ['value', 'close', 'price', 'rsi', 'macd', 'ema', 'sma', 'atr'];
          for (const propName of valueProperties) {
            const found = numericProps.find(([key]) => key.toLowerCase() === propName);
            if (found) {
              return found[1]; // Return the value
            }
          }
          
          // If no preferred property, return the first numeric value
          return numericProps[0][1];
        }
      }
      
      return null;
    })
    .filter(value => value !== undefined && value !== null);
  
  console.log(`[DataNormalizer] extractValues: Extracted ${values.length} numeric values from ${normalizedData.length} items`);
  
  // Log first few values to help with debugging
  if (values.length > 0) {
    console.log(`[DataNormalizer] First 3 values: ${values.slice(0, 3).join(', ')}`);
  }
  
  return values;
};

/**
 * Extracts just the timestamps from normalized data points
 * 
 * @param {Array} normalizedData - Array of normalized data points
 * @returns {Array} - Array of timestamp values
 */
export const extractTimestamps = (data) => {
  if (!Array.isArray(data)) {
    console.log('[DataNormalizer] extractTimestamps: Input is not an array');
    return [];
  }
  
  if (data.length === 0) {
    console.log('[DataNormalizer] extractTimestamps: Empty array provided');
    return [];
  }
  
  // Log the first few items to debug timestamp issues
  console.log(`[DataNormalizer] First few items for timestamp extraction:`, 
    JSON.stringify(data.slice(0, 2)));
  
  const timestamps = data
    .map((item, index) => {
      // Handle direct timestamp numbers
      if (typeof item === 'number') {
        // Assume this might be a timestamp itself
        return item;
      }
      
      // Handle objects
      if (item && typeof item === 'object') {
        // Standard normalized format
        if (item.timestamp !== undefined) {
          return item.timestamp;
        }
        
        // Handle result.timestamp format
        if (item.result && item.result.timestamp !== undefined) {
          return item.result.timestamp;
        }
        
        // Handle date string formats
        if (item.date) {
          return new Date(item.date).getTime();
        }
        
        // Handle time property
        if (item.time) {
          return new Date(item.time).getTime();
        }
        
        // Handle candle data with time property
        if (item.opentime) {
          return item.opentime;
        }
      }
      
      // If no timestamp found, generate sequential timestamps
      // This ensures data integrity when timeseries data is lacking timestamps
      // Use a fixed base timestamp and SEQUENTIAL intervals to ensure chronological order
      const BASE_TIMESTAMP = Date.now() - (data.length * 60000); // Start from current time minus data length in minutes
      const timestamp = BASE_TIMESTAMP + (index * 60000); // Add increasing intervals (1 minute per data point)
      
      console.log(`[DataNormalizer] Generated sequential timestamp for index ${index}: ${new Date(timestamp).toISOString()}`);
      return timestamp;
    })
    .filter(timestamp => timestamp !== undefined && timestamp !== null);
  
  // Check for duplicate timestamps and log warning
  const uniqueTimestamps = new Set(timestamps);
  if (uniqueTimestamps.size !== timestamps.length) {
    console.warn(`[DataNormalizer] WARNING: Found duplicate timestamps! ${timestamps.length - uniqueTimestamps.size} duplicates detected.`);
  }
  
  // Log the first few timestamps to verify they're different
  if (timestamps.length > 0) {
    console.log(`[DataNormalizer] First few timestamps:`, 
      timestamps.slice(0, 3).map(t => new Date(t).toISOString()));
  }
  
  console.log(`[DataNormalizer] extractTimestamps: Extracted ${timestamps.length} timestamps from ${data.length} items`);
  
  return timestamps;
};

export default {
  normalizeIndicatorData,
  normalizeSingleDataPoint,
  extractValues,
  extractTimestamps,
  extractValidTimestamps,
  isValidTimestamp
};
